<template>
  <div id="return-requests">
    <div class="p-6 loading-table" v-if="loading">
      <spinner-color />
    </div>

    <div v-else>
      <div class="mb-4">
        <vs-alert :active="!!errorMsg" color="danger" icon="new_releases">
          <span>{{ errorMsg }}</span>
        </vs-alert>
      </div>

      <div style="position: relative; width: 38%; margin-bottom: 20px;">
        <input
          v-model="search"
          @keyup.enter="doSearch()"
          @input="onSearchInput()"
          placeholder="ابحث هنا بكود الطلبية"
          class="search-mission w-full"
        />

        <!-- search input-->
        <i class="icon-advanced-search" @click="doSearch()">
          <vx-tooltip text="إضغط للبحث" delay="0.2s" position="bottom">
            <svg xmlns="http://www.w3.org/2000/svg" width="16.414" height="16.653" viewBox="0 0 16.414 16.653">
              <g id="search" transform="translate(1 1)">
                <circle cx="6" cy="6" r="6" fill="none" stroke="#6e6b7b" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
                <line x1="3.469" y1="3.469" transform="translate(10.531 10.77)" fill="none" stroke="#6e6b7b" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
              </g>
            </svg>
          </vx-tooltip>
        </i>

        <!-- reset button-->
        <i v-if="search" class="icon-reset-search" @click="resetSearch()">
          <vx-tooltip text="مسح البحث" delay="0.2s" position="bottom">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14">
              <line x1="1" y1="1" x2="13" y2="13" stroke="#6e6b7b" stroke-width="2" stroke-linecap="round"/>
              <line x1="13" y1="1" x2="1" y2="13" stroke="#6e6b7b" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </vx-tooltip>
        </i>

        <!-- errors -->
        <span class="err-search-msg" v-if="searchError">{{ searchError }}</span>
      </div>

      <vs-card>
        <div class="flex flex-wrap items-center justify-between w-full">
          <div>
            <feather-icon icon="RewindIcon" svgClasses="h-4 w-4"></feather-icon>
            <h3 class="font-bold mb-2 ml-3" style="display: inline-block; font-size: 1.125rem">
              طلبات تحتاج متابعتك
            </h3>
          </div>

          <div class="flex items-center">
            <vs-dropdown vs-trigger-click class="cursor-pointer mb-2 mr-2">
              <div
                style="font-family: 'Cairo', sans-serif !important; padding: 0.75rem !important; border-radius: 0.25rem !important;"
                class="p-4 border border-solid d-theme-border-grey-light rounded-full d-theme-dark-bg cursor-pointer flex items-center justify-between font-medium"
              >
                <span class="mr-2">عرض {{ perPage }}</span>
                <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
              </div>
              <vs-dropdown-menu>
                <ul style="min-width: 8rem">
                  <vs-dropdown-item @click="setPerPage(10)" class="m-0 p-0">
                    <li class="flex py-4 px-4 cursor-pointer profile-drop"><span>10</span></li>
                  </vs-dropdown-item>
                  <vs-divider class="m-0" />
                  <vs-dropdown-item @click="setPerPage(20)" class="m-0 p-0">
                    <li class="flex py-4 px-4 cursor-pointer profile-drop"><span>20</span></li>
                  </vs-dropdown-item>
                  <vs-divider class="m-0" />
                  <vs-dropdown-item @click="setPerPage(50)" class="m-0 p-0">
                    <li class="flex py-4 px-4 cursor-pointer profile-drop"><span>50</span></li>
                  </vs-dropdown-item>
                  <vs-divider class="m-0" />
                  <vs-dropdown-item @click="setPerPage(100)" class="m-0 p-0">
                    <li class="flex py-4 px-4 cursor-pointer profile-drop"><span>100</span></li>
                  </vs-dropdown-item>
                </ul>
              </vs-dropdown-menu>
            </vs-dropdown>

            <vs-button
              style="font-family: 'Cairo', sans-serif !important"
              type="border"
              color="primary"
              class="mb-2"
              :disabled="refreshing"
              @click="fetchReturnRequests()"
            >
              تحديث
            </vs-button>
          </div>
        </div>

        <vs-divider class="m-0 mt-4" />

        <vs-table :data="requests" noDataText="">
          <template slot="thead">
            <vs-th>الكود</vs-th>
            <vs-th>المستلم</vs-th>
            <vs-th>هاتف</vs-th>
            <vs-th>المحافظة</vs-th>
            <vs-th>المنطقة</vs-th>
            <vs-th>نوع طلب المرتجع</vs-th>
            <vs-th>سبب المرتجع</vs-th>
            <vs-th>اسم المندوب</vs-th>
            <vs-th>رقم المندوب</vs-th>
            <vs-th>الأوامر</vs-th>
          </template>

          <template slot-scope="{ data }">
            <tbody>
              <vs-tr :key="tr.id" v-for="tr in data">
                <vs-td>
                  <router-link :to="'/OrderDetails/' + tr.code">
                    <p class="font-medium truncate code">{{ tr.code }}</p>
                  </router-link>
                </vs-td>
                <vs-td><p class="font-medium truncate">{{ tr.receiver || "-" }}</p></vs-td>
                <vs-td><p class="font-medium truncate">{{ tr.phone1 || "-" }}</p></vs-td>
                <vs-td><p class="font-medium truncate">{{ tr.gov_name || "-" }}</p></vs-td>
                <vs-td><p class="font-medium truncate">{{ tr.location || "-" }}</p></vs-td>
                <vs-td><p class="font-medium truncate">{{ returnTypeText(tr.return_type) }}</p></vs-td>
                <vs-td><p class="font-medium truncate">{{ tr.return_reason_text || "-" }}</p></vs-td>
                <vs-td><p class="font-medium truncate">{{ tr.captain_name || "-" }}</p></vs-td>
                <vs-td><p class="font-medium truncate">{{ captainPhone(tr) }}</p></vs-td>
                <vs-td class="whitespace-no-wrap">
                  <vs-button
                    style="font-family: 'Cairo', sans-serif !important"
                    size="small" color="success" type="filled"
                    :disabled="!tr.return_request_type || actionLoadingOrderId === tr.id"
                    @click.stop="openConfirm(tr, 'accept')"
                  >قبول</vs-button>
                  <vs-button
                    style="font-family: 'Cairo', sans-serif !important"
                    size="small" color="danger" type="filled" class="ml-2"
                    :disabled="!tr.return_request_type || actionLoadingOrderId === tr.id"
                    @click.stop="openConfirm(tr, 'reject')"
                  >رفض</vs-button>
                </vs-td>
              </vs-tr>
            </tbody>
          </template>
        </vs-table>
      </vs-card>

      <vs-popup
        class="holamundo"
        :title="confirmTitle"
        :active.sync="confirmActive"
        @close="resetConfirm()"
      >
        <p style="padding-bottom: 1rem">{{ confirmText }}</p>
        <vs-button
          style="font-family: 'Cairo', sans-serif !important"
          @click.stop="submitConfirm()"
          :color="confirmAction === 'accept' ? 'success' : 'danger'"
          class="mr-3" type="filled" :disabled="confirmSubmitting"
        >{{ confirmAction === "accept" ? "قبول" : "رفض" }}</vs-button>
        <vs-button
          style="font-family: 'Cairo', sans-serif !important"
          @click="confirmActive = false"
          color="primary" type="filled" :disabled="confirmSubmitting"
        >إلغاء</vs-button>
      </vs-popup>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import SpinnerColor from "@/views/components/table-components/SpinnerColor2.vue";

export default {
  components: { SpinnerColor },

  data() {
    return {
      loading: true,
      refreshing: false,
      errorMsg: "",
      searchError: "",
      requests: [],
      perPage: 20,
      actionLoadingOrderId: null,
      search: "",
      confirmActive: false,
      confirmAction: null,
      confirmOrder: null,
      confirmSubmitting: false,
    };
  },

  computed: {
    confirmTitle() {
      return this.confirmAction === "accept" ? "تأكيد القبول" : "تأكيد الرفض";
    },
    confirmText() {
      if (!this.confirmOrder) return "";
      const code = this.confirmOrder.code || "";
      return this.confirmAction === "accept"
        ? `هل تريد قبول طلب المرتجع للطلب (${code})؟`
        : `هل تريد رفض طلب المرتجع للطلب (${code})؟`;
    },
  },

  watch: {
    "$route.query.code"(newCode) {
      this.search = newCode || "";
      this.fetchReturnRequests();
    },
  },

  created() {
    if (this.$route.query.code) {
      this.search = this.$route.query.code;
    }
    this.fetchReturnRequests();
  },

  methods: {
    onSearchInput() {
      if (!this.search) {
        this.resetSearch();
      }
    },

    doSearch() {
      this.searchError = "";
      const code = this.search.trim();

      if (!code) {
        this.resetSearch();
        return;
      }

      this.$router.replace({ query: { code } }).catch(() => {});
    },

    resetSearch() {
      this.search = "";
      this.searchError = "";
      this.$router.replace({ query: {} }).catch(() => {});
    },

    async fetchReturnRequests() {
      this.errorMsg = "";
      this.searchError = "";
      this.refreshing = true;

      const params = { perPage: this.perPage };
      if (this.$route.query.code) {
        params.code = this.$route.query.code;
      }

      try {
        const { data } = await axios.get("order/return-requests", { params });

        if (data && data.success === 1) {
          const response = data && data.response ? data.response : null;
          const feed = response && response.feed ? response.feed : [];
          this.requests = Array.isArray(feed) ? feed : [];

          if (params.code && this.requests.length === 0) {
            this.searchError = "لا يوجد طرد بهذا الكود، تأكد من الكود وحاول مجدداً";
          }
        } else {
          this.requests = [];
          const response = data && data.response ? data.response : null;
          this.errorMsg =
            (response && response.error_msg ? response.error_msg : '') ||
            (data && data.error_msg ? data.error_msg : '') ||
            "حدث خطأ أثناء تحميل الطلبات";
        }
      } catch (err) {
        this.requests = [];
        const res = err && err.response && err.response.data ? err.response.data : null;
        this.errorMsg =
          (res && res.error_msg ? res.error_msg : '') ||
          (res && res.message ? res.message : '') ||
          (err && err.message ? err.message : '') ||
          "حدث خطأ أثناء تحميل الطلبات";
      } finally {
        this.loading = false;
        this.refreshing = false;
      }
    },

    setPerPage(value) {
      this.perPage = value;
      this.fetchReturnRequests();
    },

    openConfirm(order, action) {
      this.confirmOrder = order;
      this.confirmAction = action;
      this.confirmActive = true;
    },

    resetConfirm() {
      if (this.confirmSubmitting) return;
      this.confirmActive = false;
      this.confirmAction = null;
      this.confirmOrder = null;
    },

    captainPhone(order) {
      if (!order) return "-";
      if (order.captain_phone1 && order.captain_phone2)
        return `${order.captain_phone1} - ${order.captain_phone2}`;
      return order.captain_phone1 || order.captain_phone2 || "-";
    },

    returnTypeText(status) {
      const map = { 7: "مدفوع", 8: "علي المرسل", 9: "الغاء" };
      return map[status] || "-";
    },

    async submitConfirm() {
      if (!this.confirmOrder || !this.confirmAction) return;
      if (!this.confirmOrder.return_request_type) return;

      this.confirmSubmitting = true;
      this.actionLoadingOrderId = this.confirmOrder.id;

      try {
        const { data } = await axios.post("order/handle-return-request", {
          order_id: this.confirmOrder.id,
          action: this.confirmAction,
          return_request_type: this.confirmOrder.return_request_type,
        });

        if (data && data.success) {
          const removedId = this.confirmOrder.id;
          this.requests = this.requests.filter((o) => o.id !== removedId);
          this.confirmActive = false;
          this.resetConfirm();
          this.$vs.notify({
            title: "نجاح",
            text: this.confirmAction === "accept" ? "تم قبول طلب المرتجع" : "تم رفض طلب المرتجع",
            color: "success",
            iconPack: "feather",
            icon: "icon-check",
          });
        } else {
          this.$vs.notify({
            title: "فشل",
            text: (data && data.error_msg ? data.error_msg : '') || (data && data.message ? data.message : '') || 'لم يتم تنفيذ العملية',
            color: "danger",
            iconPack: "feather",
            icon: "icon-alert-circle",
          });
        }
      } catch (err) {
        const res = err && err.response && err.response.data ? err.response.data : null;
        this.$vs.notify({
          title: "فشل",
          text: (res && res.error_msg ? res.error_msg : '') || (res && res.message ? res.message : '') || (err && err.message ? err.message : '') || 'لم يتم تنفيذ العملية',
          color: "danger",
          iconPack: "feather",
          icon: "icon-alert-circle",
        });
      } finally {
        this.confirmSubmitting = false;
        this.actionLoadingOrderId = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/vuexy/_loadingTable.scss";

.search-mission {
  width: 100%;
  height: 38px;
  border: 1px solid #D8D6DE;
  border-radius: 4px;
  padding: 0 2.2rem 0 2.2rem;
  color: #212121;
  margin-top: 15px;
  margin-bottom: 15px;
}

.icon-advanced-search {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  display: flex;
  align-items: center;
}

.icon-reset-search {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  display: flex;
  align-items: center;
  opacity: 0.6;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
}

.err-search-msg {
  position: absolute;
  left: 5px;
  top: 100%;
  color: #ea5455;
  font-size: 1rem;
  font-weight: 500;
  margin-top: -10px;
  white-space: nowrap;
}
</style>